import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

function canMountAddon(name) {
  const runtime = window.Addon;
  return Boolean(
    runtime
      && typeof runtime.has === 'function'
      && typeof runtime.mount === 'function'
      && runtime.has(name)
  );
}

export default function AddonSlot({
  name,
  mountProps,
  mountOptions,
  fallback,
  className,
  remountDeps,
  onMountError,
}) {
  const mountRef = useRef(null);
  const runtime = window.Addon;
  const available = canMountAddon(name);
  const slotClassName = className ? `addon-ui ${className}` : 'addon-ui';

  useEffect(() => {
    if (!available || !mountRef.current) return undefined;

    let instance;
    try {
      instance = runtime.mount(name, mountRef.current, mountProps, mountOptions);
    } catch (error) {
      if (onMountError) onMountError(error);
      return undefined;
    }

    return () => {
      if (instance && typeof instance.unmount === 'function') {
        instance.unmount();
      }
    };
    // Intentionally remount only on name + explicit dependency changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available, name, onMountError, ...remountDeps]);

  if (!available) return fallback;

  return <div ref={mountRef} className={slotClassName} data-addon-slot={name} />;
}

AddonSlot.propTypes = {
  name: PropTypes.string.isRequired,
  mountProps: PropTypes.object,
  mountOptions: PropTypes.object,
  fallback: PropTypes.node,
  className: PropTypes.string,
  remountDeps: PropTypes.arrayOf(PropTypes.any),
  onMountError: PropTypes.func,
};

AddonSlot.defaultProps = {
  mountProps: {},
  mountOptions: {},
  fallback: null,
  className: '',
  remountDeps: [],
  onMountError: null,
};
